import { create } from 'zustand';
import { api } from '@/shared/services/api';
import { announceSession, listenSession, identityMismatch, type SessionIdentity } from '../lib/session-channel';
import type { Permission } from '@softfactura/shared';

interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: string;
  /**
   * Effective permission set for the current user, computed server-side
   * (built-in role + tenant overrides + custom-role definitions). This is
   * the single source of truth — frontend gates should consume it directly
   * rather than re-resolving from `role`.
   *
   * Optional because some auth responses set `user` from a partial payload
   * that doesn't include the field; the subsequent `refreshProfile()` call
   * repopulates it. Hooks read with `permissions ?? []` and fail closed.
   */
  permissions?: readonly Permission[];
  emailVerified: boolean;
  /** Sesiune de impersonare activă (admin conectat în contul unui utilizator) —
   *  derivat server-side din cookie-ul semnat sf_impersonation. Gate-urile de
   *  verify-email NU se aplică adminului aflat în impersonare (incident 2026-07-15). */
  impersonating?: boolean;
  twofaEnabled?: boolean;
  briefingEnabled?: boolean;
  defaultStockDecrementOnIssue?: boolean;
  isSuperAdmin?: boolean;
  uiTourCompletedAt?: string | null;
  uiTourSkippedAt?: string | null;
  uiCompletedTours?: string[];
  uiSkippedTours?: string[];
}

interface AuthCompany {
  id: string;
  name: string;
  cui: string | null;
  onboardingCompleted: boolean;
}

/** Membership entry returned by /auth/profile pentru switcher-ul multi-firma. */
export interface AuthMembership {
  id: string;
  name: string;
  cui: string | null;
  role: string;
  isDefault: boolean;
  /** True pentru firma principala (cea cu care s-a inregistrat contul — membership-ul cel mai vechi). */
  isPrimary?: boolean;
  onboardingCompleted: boolean;
  deletionRequestedAt?: string | null;
  deletionScheduledFor?: string | null;
  canLeave?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  /** Compania (firma) curentă din JWT/sesiune. */
  company: AuthCompany | null;
  /** @deprecated Use `company`. */
  tenant: AuthCompany | null;
  /** Lista firmelor la care userul are membership (pentru switcher topbar). */
  tenants: AuthMembership[];
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string, twoFactorCode?: string, trustDevice?: 'month' | 'permanent') => Promise<{ requiresTwoFactor?: boolean }>;
  register: (data: { name: string; email: string; password: string; acceptTerms: boolean; acceptPrivacy: boolean; acceptUnusualTerms: boolean; acceptMarketing?: boolean; termsVersion: string; trafficSource?: any; referralCode?: string; honeypot?: string }) => Promise<void>;
  logout: () => Promise<void>;
  setAuth: (user: AuthUser, company: AuthCompany) => void;
  updatePreferences: (prefs: { briefingEnabled?: boolean; defaultStockDecrementOnIssue?: boolean }) => Promise<void>;
  initialize: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  /** Comuta la alta firma. Re-emite cookie + face hard reload pentru cache curat.
   *  redirectTo (default '/') permite redirectare custom dupa switch (ex. '/onboarding'). */
  switchTenant: (tenantId: string, redirectTo?: string) => Promise<void>;
  /** Marcheaza o firma ca default (auto-selectata la login). */
  setDefaultTenant: (tenantId: string) => Promise<void>;
}

type ProfileResponse = {
  id: string;
  name: string;
  email: string;
  role: string;
  permissions?: readonly Permission[];
  emailVerified: boolean;
  impersonating?: boolean;
  twofaEnabled?: boolean;
  briefingEnabled?: boolean;
  defaultStockDecrementOnIssue?: boolean;
  isSuperAdmin?: boolean;
  tenant: AuthCompany;
  tenants?: AuthMembership[];
  uiTourCompletedAt?: string | null;
  uiTourSkippedAt?: string | null;
  uiCompletedTours?: string[];
  uiSkippedTours?: string[];
};

function buildUserState(profile: ProfileResponse): AuthUser {
  // Detect a broken-role state: any non-empty role that resolved to zero
  // permissions points to either a deleted custom role or a server-side
  // misconfig. Surface in the console so support can spot it; UI gates
  // already fail closed (user sees an empty dashboard rather than a crash).
  if (profile.role && profile.role !== 'api_user' && (!profile.permissions || profile.permissions.length === 0)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[auth] User has role "${profile.role}" but no resolved permissions. ` +
      'The role may have been deleted or misconfigured. Contact your administrator.',
    );
  }
  return {
    id: profile.id,
    name: profile.name,
    email: profile.email,
    role: profile.role,
    permissions: profile.permissions ?? [],
    briefingEnabled: profile.briefingEnabled,
    defaultStockDecrementOnIssue: profile.defaultStockDecrementOnIssue,
    isSuperAdmin: profile.isSuperAdmin,
    emailVerified: profile.emailVerified,
    impersonating: profile.impersonating === true,
    twofaEnabled: profile.twofaEnabled || false,
    uiTourCompletedAt: profile.uiTourCompletedAt ?? null,
    uiTourSkippedAt: profile.uiTourSkippedAt ?? null,
    uiCompletedTours: profile.uiCompletedTours ?? [],
    uiSkippedTours: profile.uiSkippedTours ?? [],
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  company: null,
  tenant: null,
  tenants: [],
  isAuthenticated: false,
  isLoading: true,

  initialize: async () => {
    // Prevent double initialization (React StrictMode / multiple mounts)
    const state = useAuthStore.getState();
    if (!state.isLoading) return;
    installSessionGuards();
    try {
      const profile = await api.get<ProfileResponse>('/auth/profile');
      set({
        user: buildUserState(profile),
        company: profile.tenant,
        tenant: profile.tenant,
        tenants: profile.tenants ?? [],
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      set({ isLoading: false, isAuthenticated: false });
    }
  },

  refreshProfile: async () => {
    try {
      const profile = await api.get<ProfileResponse>('/auth/profile');
      set({
        user: buildUserState(profile),
        company: profile.tenant,
        tenant: profile.tenant,
        tenants: profile.tenants ?? [],
        isAuthenticated: true,
      });
    } catch {
      // Silently fail on refresh
    }
  },

  login: async (email, password, twoFactorCode, trustDevice) => {
    const response = await api.post<{
      requiresTwoFactor?: boolean;
      user?: AuthUser;
      tenant?: AuthCompany;
      tenants?: AuthMembership[];
    }>('/auth/login', { email, password, twoFactorCode, trustDevice });

    if (response.requiresTwoFactor) {
      return { requiresTwoFactor: true };
    }

    if (response.user) {
      set({
        user: response.user,
        company: response.tenant ?? null,
        tenant: response.tenant ?? null,
        tenants: response.tenants ?? [],
        isAuthenticated: true,
      });
      // Hardening multi-tab: taburile vechi (posibil pe ALT cont) afla acum
      // si se reincarca pe sesiunea noua, in loc sa o rescrie prin refresh.
      announceSession({ userId: response.user.id, tenantId: response.tenant?.id ?? null });
    }

    return {};
  },

  register: async (data) => {
    const response = await api.post<{
      user: AuthUser;
      tenant: AuthCompany;
      tenants?: AuthMembership[];
    }>('/auth/register', data);

    set({
      user: response.user,
      company: response.tenant,
      tenant: response.tenant,
      tenants: response.tenants ?? [],
      isAuthenticated: true,
    });
    announceSession({ userId: response.user.id, tenantId: response.tenant?.id ?? null });
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore errors on logout
    }

    set({
      user: null,
      company: null,
      tenant: null,
      tenants: [],
      isAuthenticated: false,
    });
    announceSession({ userId: null, tenantId: null });

    // Curatam preferintele de aspect din localStorage la logout. Daca alt
    // user se autentifica pe acelasi browser, bootstrap script-ul nu va mai
    // citi paleta/fontul fostului user — defaults brut → fetch din DB →
    // preferintele noului user. Tranzitie curata, fara flash-uri duble.
    //
    // De asemenea, curatam draft-urile locale (`invoice-draft-backup:*` v1+v2,
    // `nir.*`). Fara asta, un user pe browser shared (sediu, internet cafe)
    // ar lasa drafturi cu date confidentiale (clienti, sume) in localStorage,
    // vizibile la urmatoarea autentificare.
    try {
      localStorage.removeItem('sf-theme-prefs');
      localStorage.removeItem('sf-font-prefs');
      localStorage.removeItem('sf-sidebar-groups');
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (k.startsWith('invoice-draft-backup:') || k.startsWith('nir.')) {
          toRemove.push(k);
        }
      }
      toRemove.forEach((k) => { try { localStorage.removeItem(k); } catch {} });
    } catch { /* sandbox/quota — ignore */ }
  },

  updatePreferences: async (prefs) => {
    const current = useAuthStore.getState().user;
    if (!current) return;
    // Optimistic update
    set({ user: { ...current, ...prefs } });
    try {
      await api.patch('/auth/preferences', prefs);
    } catch (err) {
      // Revert on failure
      set({ user: current });
      throw err;
    }
  },

  setAuth: (user, tenant) => {
    set({ user, company: tenant, tenant, isAuthenticated: true, isLoading: false });
  },

  switchTenant: async (tenantId: string, redirectTo: string = '/') => {
    // Backend re-emite cookie cu currentTenantId nou. Hard reload =>
    // React Query cache + tot state-ul React se reseteaza. Pattern identic
    // cu ImpersonationBanner: schimbare context activa.
    // redirectTo permite redirectare custom (ex. '/onboarding' pentru firma
    // noua care nu are setarile completate inca).
    await api.post('/auth/switch-tenant', { tenantId });
    // Celelalte taburi raman altfel pe firma veche cu cookie-ul firmei noi.
    const me = useAuthStore.getState().user;
    announceSession({ userId: me?.id ?? null, tenantId });
    if (typeof window !== 'undefined') {
      // Salveaza numele firmei in sessionStorage ca toast-ul post-reload sa
      // poata afisa "Acum vezi {firma}". sessionStorage supravietuieste
      // hard reload-ului dar e pe-tab, deci nu polueaza alte tab-uri.
      const target = useAuthStore.getState().tenants.find((t) => t.id === tenantId);
      if (target) {
        try {
          sessionStorage.setItem('tenantSwitchedTo', target.name);
        } catch {
          // sessionStorage poate fi blocat (private browsing) — ignoram, e doar UI
        }
      }
      window.location.href = redirectTo;
    }
  },

  setDefaultTenant: async (tenantId: string) => {
    await api.post('/tenants/set-default', { tenantId });
    // Update local state ca sa se vada efectul imediat in UI
    set((state) => ({
      tenants: state.tenants.map((t) => ({ ...t, isDefault: t.id === tenantId })),
    }));
  },

}));

// ── Hardening multi-tab (2026-06-11) ─────────────────────────────────────────
// O singură instalare per pagină: (1) listener cross-tab — alt tab a schimbat
// sesiunea → dacă identitatea diferă de a noastră, reload (preluăm sesiunea
// din cookie); (2) callback pe clientul API — după orice refresh de token,
// identitatea din răspuns trebuie să corespundă celei afișate, altfel reload.
// Reload-ul re-rulează initialize() → profil = cookie → stare consistentă;
// fără buclă (identitățile coincid după reload).
let sessionGuardsInstalled = false;

function currentIdentity(): SessionIdentity {
  const st = useAuthStore.getState();
  return { userId: st.user?.id ?? null, tenantId: st.tenant?.id ?? null };
}

function installSessionGuards(): void {
  if (sessionGuardsInstalled || typeof window === 'undefined') return;
  sessionGuardsInstalled = true;

  listenSession((incoming) => {
    const st = useAuthStore.getState();
    // În timpul bootstrap-ului nu avem încă identitate de comparat.
    if (st.isLoading) return;
    if (identityMismatch(currentIdentity(), incoming)) {
      window.location.reload();
    }
  });

  api.onRefreshIdentity = (incoming) => {
    const st = useAuthStore.getState();
    if (st.isLoading || !st.user) return; // bootstrap — profilul va fi oricum al cookie-ului
    if (identityMismatch(currentIdentity(), incoming)) {
      window.location.reload();
    }
  };
}
